import type { ConsentSignatureRow, ConsentVersionRow, HandbookAckRow, HandbookVersionRow, Role } from '@routeback/core';

/** The wording drivers agree to when a company hasn't written its own (the Driver Portal's, without the company name baked in). */
export function defaultAckText(company: string): string {
  const c = company.trim() || 'the company';
  return `I acknowledge that I have received a copy of the ${c} Employee Handbook. I understand that it is my responsibility to read it, `
    + 'to follow the policies and procedures it describes, and to ask my manager about anything I do not understand. '
    + `I understand that ${c} may change, add to, or remove any of these policies at any time, and that this handbook is not a contract of employment.`;
}

export interface Pending {
  handbook: HandbookVersionRow | null;
  consents: ConsentVersionRow[];
}

/** Active documents this person hasn't signed yet. */
export function pendingFor(
  userId: string,
  handbooks: HandbookVersionRow[], acks: HandbookAckRow[],
  consents: ConsentVersionRow[], sigs: ConsentSignatureRow[],
): Pending {
  const hb = handbooks.find((h) => h.active && !h.deleted_at) ?? null;
  const signedHb = !!hb && acks.some((a) => !a.deleted_at && a.user_id === userId && a.handbook_version_id === hb.id);
  const live = consents.filter((c) => c.active && !c.deleted_at).sort((a, b) => a.kind.localeCompare(b.kind));
  return {
    handbook: hb && !signedHb ? hb : null,
    consents: live.filter((c) => !sigs.some((s) => !s.deleted_at && s.user_id === userId && s.consent_version_id === c.id)),
  };
}

/**
 * Drivers are locked out until they sign (like the Driver Portal); managers,
 * admins and owners only see a reminder for the consent; mechanics never sign.
 */
export function gate(role: Role, p: Pending): { blocked: boolean; remind: boolean } {
  const any = !!p.handbook || p.consents.length > 0;
  if (role === 'driver') return { blocked: any, remind: false };
  if (role === 'mechanic') return { blocked: false, remind: false };
  return { blocked: false, remind: p.consents.length > 0 };
}

/** Pages a driver can always open, signed or not (the plugin let accident reports and the lineup through). */
export const ALWAYS_OPEN = [/^\/accident$/, /^\/requests\/accident\//, /^\/lineup$/, /^\/signoff$/, /^\/me$/, /^\/settings(\/|$)/];
export const alwaysOpen = (path: string): boolean => ALWAYS_OPEN.some((r) => r.test(path));
